/* eslint-disable no-mixed-spaces-and-tabs */
import {apiEndpoint} from "../constants/api.constants";

type userRole = "ADMIN" | "VET" | "OWNER" | "TECH" | "PARTNER";

export const setSessionStorage = (key, data) => {
    sessionStorage.setItem(key, data);
};

class Api {
    public url: string = apiEndpoint;
    private static instance: Api;
    public token: string;
    public userId: string;
    public isLoggedIn: boolean;
    public role: userRole;

    public setAuthToLocal(authData) {
    	if (authData.authenticationToken) {
    		this.token = authData.authenticationToken.token;
    		this.userId = authData.authenticationToken.userId;
    	} else {
    		this.token = authData.token;
    		this.userId = authData.userId;
    		this.role = authData.role;
    	}
    	localStorage.setItem("auth", JSON.stringify(authData));
    }

    public setAuthToCookie(authData) {
    	this.role = authData.role;
    	this.token = authData.token;
    	this.userId = authData.userId;
    	document.cookie = `token=${authData.token}; expiresAt=${authData.expiresAt}`;
    	document.cookie = `userId=${authData.userId}; expiresAt=${authData.expiresAt}`;
    	document.cookie = `role=${authData.role}; expiresAt=${authData.expiresAt}`;
    }

    private checkTokenInLocalStorage = (): boolean => {
    	if (localStorage.getItem("auth")) return true;
    	return false;
    };

    private checkTokenInCookie = (): boolean => {
    	return document.cookie.search("token") >= 0;
    };

    private checkUserIdInCookie = (): boolean => {
    	return document.cookie.search("userId") >= 0;
    };

    private getAuthFromLocalStorage = () => {
    	try {
    		const result = localStorage.getItem("auth");
    		const {authenticationToken} = JSON.parse(result);
    		if (authenticationToken) {
    			return {
    				token: authenticationToken.token,
    				userId: authenticationToken.userId,
    				role: authenticationToken.role,
    			};
    		} else {
    			const {token, userId, role} = JSON.parse(result);
    			return {token, userId, role};
    		}
    	} catch (err) {
    		throw new Error("There is no token in local storage");
    	}
    };

    private getTokenFromCookies = (): string => {
    	const tokenIndex = document.cookie.search("token");
    	if (tokenIndex >= 0) {
    		const indexOfTokenStart = document.cookie.indexOf("=", tokenIndex) + 1;
    		const indexOfTokenEnd = document.cookie.indexOf(";", tokenIndex);
    		return document.cookie.slice(indexOfTokenStart, indexOfTokenEnd);
    	} else {
    		throw new Error("There is no token in cookies");
    	}
    };

    private getUserIdFromCookies = (): string => {
    	const userIdIndex = document.cookie.search("userId");
    	if (userIdIndex >= 0) {
    		const indexOfuserIdStart = document.cookie.indexOf("=", userIdIndex) + 1;
    		const indexOfuserIdEnd = document.cookie.indexOf(";", userIdIndex);
    		if (indexOfuserIdEnd > -1) {
    			return document.cookie.slice(indexOfuserIdStart, indexOfuserIdEnd);
    		} else {
    			return document.cookie.slice(indexOfuserIdStart);
    		}
    	} else {
    		throw new Error("There is no userId in cookies");
    	}
    };

    private getRoleFromCookies = (): userRole => {
    	const roleIndex = document.cookie.search("role");
    	if (roleIndex >= 0) {
    		const indexOfRoleStart = document.cookie.indexOf("=", roleIndex) + 1;
    		const indexOfRoleEnd = document.cookie.indexOf(";", roleIndex);
    		if (indexOfRoleEnd > -1) {
    			return document.cookie.slice(
    				indexOfRoleStart,
    				indexOfRoleEnd
    			) as userRole;
    		} else {
    			return document.cookie.slice(indexOfRoleStart) as userRole;
    		}
    	} else {
    		// throw new Error("There is no role in cookies");
    		console.log("there is no role in cookies");
    	}
    };

    public clearAuth = (): void => {
    	this.token = null;
    	this.userId = "";
    	document.cookie = "token= ; expires = Thu, 01 Jan 1970 00:00:00 GMT";
    	document.cookie = "userId= ; expires = Thu, 01 Jan 1970 00:00:00 GMT";
    	localStorage.removeItem("auth");
    	this.isLoggedIn = false;
    };

    constructor() {
    	if (this.checkTokenInLocalStorage()) {
    		const {token, userId, role} = this.getAuthFromLocalStorage();
    		this.token = token;
    		this.userId = userId;
    		this.role = role;
    	} else if (this.checkTokenInCookie()) {
    		this.token = this.getTokenFromCookies();
    		if (this.checkUserIdInCookie()) {
    			this.userId = this.getUserIdFromCookies();
    			this.role = this.getRoleFromCookies();
    		}
    	} else {
    		this.token = null;
    	}
    	this.isLoggedIn = !!this.token;
    }

    private async makeFetch(
    	_url: string,
    	method: string,
    	params: object = {},
    	body = null,
    	isJson = true,
    	credentials: RequestCredentials = "same-origin",
    	emptyResp: boolean = false,
    ) {
    	let url = new URL(_url);
    	Object.keys(params).forEach((key) => {
    		if (Array.isArray(params[key])) {
    			params[key].forEach((item) => url.searchParams.append(key, item));
    		} else {
    			url.searchParams.append(key, params[key]);
    		}
    	});
    	const token = this.token;
    	const headers = {
    		"Content-Type": isJson ? "application/json;" : "application/x-www-form-urlencoded",
    	};
    	if (token) {
    		const bearer = `Bearer ${token}`;
    		Object.assign(headers, {Authorization: bearer});
    	}
    	const requestUrl = url.toString();
    	const response = await fetch(requestUrl, {
    		method,
    		headers,
    		credentials,
    		body,
    	});
    	let result = null;
    	if (!emptyResp) {
    		result = await response.json();
    	}
    	if (!response.ok) {
    		if (response.status === 401) {
    			this.clearAuth();
    			window.location.replace(`${window.location.origin}/login?401`);
    		}
            throw result;
    	}
    	return result;
    }

    public async fetch({
    	_url = "",
    	method = "GET",
    	params = {},
    	body = null,
    	isJson = true,
    }) {
    	let url = new URL(`${apiEndpoint}${_url}`);
    	Object.keys(params).forEach((key) => {
    		if (Array.isArray(params[key])) {
    			params[key].forEach((item) => url.searchParams.append(key, item));
    		} else {
    			url.searchParams.append(key, params[key]);
    		}
    	});
    	const token = this.token;
    	const headers = {
    		"Content-Type": isJson ? "application/json;" : "application/x-www-form-urlencoded",
    	};
    	if (token) {
    		const bearer = `Bearer ${token}`;
    		Object.assign(headers, {Authorization: bearer});
    	}
    	const requestUrl = url.toString();
    	if (body && isJson) {
    		body = JSON.stringify(body);
    	}
    	const response = await fetch(requestUrl, {
    		method,
    		headers,
    		body,
    	});
    	let result = null;
    	if (response.status === 204){
    		result = null;
    	} else {
    		result = await response.json();
    	}
    	if (!response.ok) {
    		if (response.status === 401) {
    			this.clearAuth();
    			window.location.replace(`${window.location.origin}/login?401`);
    		}
    		throw result;
    	}
    	return result;
    }

    public async getProfileById(id) {
    	const url = `${apiEndpoint}profiles/${id}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getProfiles(params) {
    	const url = `${apiEndpoint}profiles`;
    	const response = await this.makeFetch(url, "GET", params);
    	return response;
    }

    public async getVetsForImmediateAppointment(params) {
    	const url = `${apiEndpoint}profiles/immediate/available`;
    	const response = await this.makeFetch(url, "GET", params);
    	return response;
    }

    public async getPartnerById(id) {
    	const url = `${apiEndpoint}partners/${id}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getPartnerStatistics(params) {
    	const url = `${apiEndpoint}appointmentStatistics`;
    	const response = await this.makeFetch(url, "GET", params);
    	return response;
    }

    public async getPartners() {
    	const url =`${apiEndpoint}partners`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getUserById(id) {
    	const url = `${apiEndpoint}users/${id}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getAnimal(ownerId, practiceCode = "") {
    	const url = `${apiEndpoint}animals`;
    	const response = await this.makeFetch(url, "GET", {
    		ownerId,
    		practiceCode,
    	});
    	return response;
    }

    public async getCustomers() {
    	const url = `${apiEndpoint}customers`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getApointmentById(id: number) {
    	const url = `${apiEndpoint}appointments/${id}?includeUserDetails=true`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getAppointmentWithDetails(id: number) {
    	const url = `${apiEndpoint}appointments/${id}/detail`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async checkForReferralInvite() {
    	const url = `${apiEndpoint}referralCodeRequests?status=INVITED`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async createAppointmentOnBehalfOfUser(userId, body) {
    	const url = `${apiEndpoint}appointmentRequests/behalfofuser/${userId}`;
    	const bearer = `Bearer ${this.token}`;
    	const response = await fetch(url, {
    		method: "POST",
    		body: JSON.stringify(body),
    		headers: { "Authorization": bearer, "Content-Type": "application/json"},
    	});
    	return response;
    }
    public async createCreditCard(body) {
    	const url = `${apiEndpoint}customerCards`;
    	const response = await this.makeFetch(url, "POST", {isDefault: true}, JSON.stringify(body));
    	return response;
    }

    public async updateCreditCard(id, isDefault) {
    	const url = `${apiEndpoint}customerCards/${id}`;
    	const response = await this.makeFetch(url, "PATCH", {},JSON.stringify({isDefault}));
    	return response;
    }

    public async getCreditCards() {
    	const url = `${apiEndpoint}customerCards`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }
    public async deleteCreditCards(id) {
    	const url = `${apiEndpoint}customerCards/${id}`;
    	const response = await this.makeFetch(url, "DELETE");
    	return response;
    }

    public async createCustomer(body) {
    	const url = `${apiEndpoint}customers`;
    	const bearer = `Bearer ${this.token}`;
    	const response = await fetch(url, {
    		method: "POST",
    		body: JSON.stringify(body),
    		headers: { "Authorization": bearer, "Content-Type": "application/json"}
    	});
    	return response;
    }

    public async addVetSchedule(vetId, body) {
    	const url = `${apiEndpoint}profiles/${vetId}/workdays/bulk`;
    	const bearer = `Bearer ${this.token}`;
    	const response = await fetch(url, {
    		method: "POST",
    		body: JSON.stringify(body),
    		headers: { "Authorization": bearer, "Content-Type": "application/json"}
    	});
    	return response;
    }

    public async getConsentURLs(id) {
    	const url = `${apiEndpoint}consents/${id}/upload`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getLiscenseUrls(vetId) {
    	const url = `${apiEndpoint}profiles/upload/licenses/${vetId}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async deleteLicense(vetId, imageId) {
    	const url = `${apiEndpoint}profiles/${vetId}/aws/licenses/${imageId}`;
    	const response = await this.makeFetch(url, "DELETE");
    	//return response;
    }

    public async addConsentForm(params) {
    	const url = `${apiEndpoint}consents`;
    	const bearer = `Bearer ${this.token}`;
    	const response = await fetch(url, {
    		method: "POST",
    		body: JSON.stringify(params),
    		headers: { "Authorization": bearer, "Content-Type": "application/json"}
    	});
    	return response;
    }

    public async patchAddress(id, params) {
    	const url = `${apiEndpoint}addresses/${id}`;
    	const bearer = `Bearer ${this.token}`;
    	const response = await fetch(url, {
    		method: "PATCH",
    		body: JSON.stringify(params),
    		headers: { "Authorization": bearer, "Content-Type": "application/json"}
    	});
    	return response;
    }

    public async getReferralCode(id) {
    	const url = `${apiEndpoint}referralCodes?userId=${id}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getReferralStats(){
    	const url = `${apiEndpoint}referralCodes/statistics`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async postSubscriptionPayment(body) {
    	const response = await this.makeFetch(
    		`${apiEndpoint}subscriptions`,
    		"POST",
    		{},
    		JSON.stringify(body)
    	);
    	return response;
    }

    public async getAddressAppointment(userId) {
    	const response = await this.makeFetch(
    		`${apiEndpoint}addresses`,
    		"GET",
    		{userId: userId}
    	);
    	return response;
    }

    public async postAgreement(params) {
    	const bearer = `Bearer ${this.token}`;
    	const response = await fetch(`${apiEndpoint}agreements`, {
    		method: "POST",
    		body: JSON.stringify(params),
    		headers: { "Authorization": bearer, "Content-Type": "application/json"}
    	});
    	return response;
    }

    public async getSquareoauth() {
    	const url = `${apiEndpoint}squareoauth`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getSquareOauthLink() {
    	const url = `${apiEndpoint}squareoauth/authorize?redirect=false`;
		const bearer = `Bearer ${this.token}`;
		const response = await fetch(url, {
    		method: "GET",
    		headers: { "Authorization": bearer }
    	}).then((response) => response.json());
    	return response;
    }

    public async getAgorakeys(appointmentId) {
    	const url = `${apiEndpoint}agora/token/${appointmentId}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async getUserEmailAvaliability(email) {
    	const url = `${apiEndpoint}users/avaliabilities/${email}`;
    	const response = await this.makeFetch(url, "GET");
    	return response;
    }

    public async uploadToAWS(url, body) {
    	const response = await fetch(url, {
    		method: "PUT",
    		body,
    		headers: {
    			"Content-Type": "image/jpeg",
    		},
    	});
    	return response;
    }

    static getInstance(): Api {
    	if (!Api.instance) {
    		Api.instance = new Api();
    	}

    	return Api.instance;
    }
}
export default Api.getInstance();
